import { Fragment, cloneElement, createElement, isValidElement, useMemo, type ReactNode } from 'react';
import ReactMarkdown, { type Components } from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { Box, type SxProps, type Theme } from '@mui/material';

const proseSx: SxProps<Theme> = {
  fontFamily: 'var(--font-sans)',
  fontSize: 'var(--font-size-md)',
  color: 'var(--color-ink-2)',
  lineHeight: 'var(--line-height-relaxed)',
  '& p': { margin: '0 0 12px', lineHeight: 1.62, color: 'var(--color-ink-2)', textWrap: 'pretty' },
  '& p:last-child': { marginBottom: 0 },
  '& h1,& h2': { mt: 2.5, mb: 1, fontWeight: 700, fontSize: 'var(--font-size-xl)', letterSpacing: '-0.015em', color: 'var(--color-ink)' },
  '& h3,& h4': { mt: 2, mb: 0.75, fontWeight: 700, fontSize: 16, color: 'var(--color-ink)' },
  '& ul,& ol': { pl: 2.5, mb: 1.5, display: 'flex', flexDirection: 'column', gap: '8px' },
  '& li': { fontSize: 'var(--font-size-md)', lineHeight: 1.55, color: 'var(--color-ink-2)' },
  '& code': {
    fontFamily: 'var(--font-mono)',
    fontSize: '0.85em',
    bgcolor: 'var(--color-surface-2)',
    px: 0.5,
    borderRadius: '5px',
  },
  '& pre': {
    bgcolor: 'var(--color-surface-2)',
    p: 1.5,
    borderRadius: 'var(--radius-sm)',
    overflowX: 'auto',
    '& code': { bgcolor: 'transparent', px: 0 },
  },
  '& blockquote': {
    borderLeft: '3px solid var(--color-primary-line)',
    pl: 2,
    color: 'var(--color-muted)',
    my: 1.5,
    fontStyle: 'italic',
  },
  // wrap wide tables so they scroll instead of overflowing the page
  '& table': {
    display: 'block',
    width: 'max-content',
    maxWidth: '100%',
    overflowX: 'auto',
    borderCollapse: 'collapse',
    my: 1.5,
    fontSize: 'var(--font-size-sm)',
  },
  '& th,& td': {
    border: '1px solid var(--color-line)',
    px: 1,
    py: 0.75,
    textAlign: 'left',
    verticalAlign: 'top',
  },
  '& th': {
    fontWeight: 700,
    bgcolor: 'var(--color-surface-2)',
    color: 'var(--color-ink)',
    whiteSpace: 'nowrap',
  },
  '& tbody tr:nth-of-type(even)': {
    bgcolor: 'var(--color-surface-2)',
  },
  '& mark': {
    bgcolor: '#fde047',
    color: 'inherit',
    borderRadius: '2px',
  },
};

// Block-level tags that can contain leaf text (directly or via nested inline
// elements like <strong>/<a>/<code>). Overriding just these is enough to reach
// every text node react-markdown produces.
const HIGHLIGHT_BLOCK_TAGS = ['p', 'li', 'h1', 'h2', 'h3', 'h4', 'h5', 'h6', 'blockquote', 'td', 'th'] as const;

function escapeRegExp(value: string): string {
  return value.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
}

function buildHighlightRegex(terms?: string[]): RegExp | null {
  if (!terms || terms.length === 0) return null;
  const escaped = Array.from(new Set(terms.map((t) => t.trim()).filter(Boolean))).map(escapeRegExp);
  if (escaped.length === 0) return null;
  return new RegExp(`(${escaped.join('|')})`, 'gi');
}

function highlightNode(node: ReactNode, regex: RegExp): ReactNode {
  if (typeof node === 'string') {
    const parts = node.split(regex);
    if (parts.length <= 1) return node;
    return parts.map((part, i) => (i % 2 === 1 && part ? <mark key={i}>{part}</mark> : part));
  }
  if (Array.isArray(node)) {
    return node.map((child, i) => (
      <Fragment key={i}>{highlightNode(child, regex)}</Fragment>
    ));
  }
  if (isValidElement<{ children?: ReactNode }>(node) && node.props.children != null) {
    return cloneElement(node, undefined, highlightNode(node.props.children, regex));
  }
  return node;
}

// Open links in a new tab instead of navigating away from the app.
const linkComponents: Components = {
  a: ({ node: _node, ...props }) => <a {...props} target="_blank" rel="noopener noreferrer" />,
};

type BlockProps = { node?: unknown; children?: ReactNode } & Record<string, unknown>;

function buildHighlightComponents(regex: RegExp): Components {
  const components: Record<string, (props: BlockProps) => ReactNode> = {};
  for (const tag of HIGHLIGHT_BLOCK_TAGS) {
    components[tag] = ({ node: _node, children, ...rest }) =>
      createElement(tag, rest, highlightNode(children, regex));
  }
  return components as Components;
}

/** Renders Markdown (with GFM tables/strikethrough/etc.) using the app's standard prose styling. */
export function MarkdownContent({
  children,
  sx,
  highlightTerms,
}: {
  children: string;
  sx?: SxProps<Theme>;
  /** Words to wrap in <mark> wherever they appear in the rendered text. */
  highlightTerms?: string[];
}) {
  const components = useMemo(() => {
    const regex = buildHighlightRegex(highlightTerms);
    return regex ? { ...linkComponents, ...buildHighlightComponents(regex) } : linkComponents;
  }, [highlightTerms]);

  return (
    <Box sx={sx ? [proseSx, sx] : proseSx}>
      <ReactMarkdown remarkPlugins={[remarkGfm]} components={components}>{children}</ReactMarkdown>
    </Box>
  );
}
